import { apiGet, apiGetBlob, apiPost, apiPut, apiPatch, apiPostForm } from './http'
import type {
  MeResponse,
  RawUser,
  ContractsIndexResponse,
  ContractShowResponse,
  RawContract,
  BankingAccountDetailFullResponse,
  BankingAccountDetailResponse,
  RawBankingAccountDetail,
  RawBankingAccountDetailFull,
  RawDocument,
  RawDocumentKind,
  DocumentResponse,
  OperationsIndexResponse,
  RawOperation,
} from '@/types/api'

/**
 * Typed boundary to the connected-space API (current user, contracts).
 *
 * Authenticated calls (`{ auth: true }`) attach the session bearer token and go
 * through the transport's refresh/replay handling. Transport + payload shaping
 * only — mapping to view-models lives in `src/mappers`, reactive state in the
 * composables.
 */

/** Fetch the current authenticated user (`GET /me`). */
export async function getMe(signal?: AbortSignal): Promise<RawUser> {
  const res = await apiGet<MeResponse>('/me', { auth: true, signal })
  return res.user
}

/**
 * Change the current user's password (`PATCH /users`). One-shot authenticated
 * change — no email round-trip. Throws `ApiError` 422 when the current password
 * is wrong (`errors.current_password`) or the new one fails validation
 * (`errors.password`).
 */
export async function updatePassword(
  currentPassword: string,
  password: string,
  passwordConfirmation: string,
  signal?: AbortSignal,
): Promise<void> {
  await apiPatch<MeResponse>(
    '/users',
    {
      user: {
        current_password: currentPassword,
        password,
        password_confirmation: passwordConfirmation,
      },
    },
    { auth: true, signal },
  )
}

/**
 * Update the current user's phone number + address (`PATCH /users`). First and
 * last names are read-only and no longer editable. The address is created when
 * absent and updated otherwise (backend handles it). Returns the updated user.
 * Throws `ApiError` 422 on validation errors.
 */
export async function updateProfile(
  profile: { phoneNumber?: string; socialSecurityNumber?: string },
  address?: { line1: string; line2: string; postalCode: string; city: string; country: string },
  signal?: AbortSignal,
): Promise<RawUser> {
  const user: Record<string, unknown> = {}
  if (profile.phoneNumber !== undefined) user.phone_number = profile.phoneNumber
  // Only ever sent when the member had none: a NIR already on file is never editable, so the
  // key is left out entirely rather than sent unchanged. That keeps the request honest about
  // what it is asking to change, and leaves the back free to refuse any attempt to overwrite.
  if (profile.socialSecurityNumber) user.social_security_number = profile.socialSecurityNumber
  if (address) {
    user.address = {
      address_line1: address.line1,
      address_line2: address.line2,
      zipcode: address.postalCode,
      city: address.city,
      country: address.country,
    }
  }
  const res = await apiPatch<MeResponse>('/users', { user }, { auth: true, signal })
  return res.user
}

/**
 * Update the current user's commercial prospecting consent (`PATCH /users`).
 * Kept apart from `updateProfile`: the consent is given or withdrawn on its own,
 * without the current password, so it is never bundled with a profile edit.
 * Throws `ApiError` 422 when the value is not a boolean.
 */
export async function updateProspectingOptIn(
  optIn: boolean,
  signal?: AbortSignal,
): Promise<RawUser> {
  const res = await apiPatch<MeResponse>(
    '/users',
    { user: { prospecting_opt_in: optIn } },
    { auth: true, signal },
  )
  return res.user
}

/**
 * Upload a document for the connected member (`POST /documents`, multipart).
 * The backend attaches it to the current user (ownership is server-side) and
 * defaults the status to `to_validate`. Returns the created raw document.
 * Throws `ApiError` (422 on validation: wrong file type/size or missing field).
 */
export async function uploadDocument(
  file: File,
  kind: RawDocumentKind,
  signal?: AbortSignal,
): Promise<RawDocument> {
  const form = new FormData()
  form.append('document', file)
  form.append('document_kind', kind)
  const res = await apiPostForm<DocumentResponse>('/documents', form, { auth: true, signal })
  return res.document
}

/** Fetch the current user's contracts (`GET /contracts`). */
export async function listContracts(signal?: AbortSignal): Promise<RawContract[]> {
  const res = await apiGet<ContractsIndexResponse>('/contracts', { auth: true, signal })
  return res.contracts
}

/** Fetch a single contract by id or contract number (`GET /contracts/:id`). */
export async function getContract(id: string, signal?: AbortSignal): Promise<RawContract> {
  const res = await apiGet<ContractShowResponse>(`/contracts/${id}`, { auth: true, signal })
  return res.contract
}

/** Request termination of a contract (`PUT /contracts/:id/request_terminating`).
 *  Returns the updated contract. Throws `ApiError` (422 when not allowed). */
export async function requestTerminating(id: string, signal?: AbortSignal): Promise<RawContract> {
  const res = await apiPut<ContractShowResponse>(
    `/contracts/${id}/request_terminating`,
    undefined,
    { auth: true, signal },
  )
  return res.contract
}

/**
 * Fetch the member's health reimbursements (`GET /operations`).
 *
 * Reimbursements are the `refund` / `care` operations; the filter is applied
 * server-side so the other kinds (expenses, offers, accommodation) never reach
 * the client. Rails reads `filters[x_in][]` as an array, which is what
 * `FilterOperations` ransacks on.
 */
export async function listReimbursements(signal?: AbortSignal): Promise<RawOperation[]> {
  const res = await apiGet<OperationsIndexResponse>('/operations', {
    auth: true,
    query: { 'filters[kind_in][]': 'refund', 'filters[category_in][]': 'care' },
    signal,
  })
  return res.operations
}

/** Download the member's third-party-payer e-card (`GET /ecards/download`). */
export async function downloadEcard(format: 'png' | 'pdf' = 'pdf', signal?: AbortSignal): Promise<Blob> {
  return apiGetBlob('/ecards/download', { auth: true, query: { format }, signal })
}

/** Download a contract certificate as a PDF blob (`GET /contracts/:id/generate_certificate`). */
export async function downloadCertificate(id: string, signal?: AbortSignal): Promise<Blob> {
  return apiGetBlob(`/contracts/${id}/generate_certificate`, { auth: true, signal })
}

/**
 * Update the refund banking account detail of a contract
 * (`PATCH /contracts/:id/banking_account_detail/:banking_account_detail_id`).
 * Returns the updated (masked) detail. Throws `ApiError` 422 on invalid IBAN/BIC.
 */
export async function updateBankingAccountDetail(
  contractId: string,
  bankingAccountDetailId: string,
  payload: { iban: string; bic: string; ownerFullname: string; bankName?: string },
  signal?: AbortSignal,
): Promise<RawBankingAccountDetail> {
  const res = await apiPatch<BankingAccountDetailResponse>(
    `/contracts/${contractId}/banking_account_detail/${bankingAccountDetailId}`,
    {
      banking_account_detail: {
        iban: payload.iban,
        bic: payload.bic,
        owner_fullname: payload.ownerFullname,
        ...(payload.bankName ? { bank_name: payload.bankName } : {}),
      },
    },
    { auth: true, signal },
  )
  return res.banking_account_detail
}

/**
 * Create the refund banking account detail of a contract
 * (`POST /contracts/:id/banking_account_detail`). Used to register a member's
 * first RIB when none exists yet. Returns the created (masked) detail. Throws
 * `ApiError` 422 on invalid IBAN/BIC.
 */
export async function createBankingAccountDetail(
  contractId: string,
  payload: { iban: string; bic: string; ownerFullname: string; bankName?: string },
  signal?: AbortSignal,
): Promise<RawBankingAccountDetail> {
  const res = await apiPost<BankingAccountDetailResponse>(
    `/contracts/${contractId}/banking_account_detail`,
    {
      banking_account_detail: {
        kind: 'refund',
        iban: payload.iban,
        bic: payload.bic,
        owner_fullname: payload.ownerFullname,
        ...(payload.bankName ? { bank_name: payload.bankName } : {}),
      },
    },
    { auth: true, signal },
  )
  return res.banking_account_detail
}

/** Fetch the full (unmasked) banking account detail for a contract — used on
 *  demand when the user reveals or edits the IBAN (`GET …/banking_account_detail/:id`). */
export async function getBankingAccountDetail(
  contractId: string,
  bankingAccountDetailId: string,
  signal?: AbortSignal,
): Promise<RawBankingAccountDetailFull> {
  const res = await apiGet<BankingAccountDetailFullResponse>(
    `/contracts/${contractId}/banking_account_detail/${bankingAccountDetailId}`,
    { auth: true, signal },
  )
  return res.banking_account_detail
}
